import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

function Slider({ q, value, onChange, t }) {
  const v = value ?? 50;
  return (
    <div>
      <input type="range" min="0" max="100" value={v} data-testid={`slider-${q.id}`} onChange={(e) => onChange(Number(e.target.value))} className="decide-slider mt-5" />
      <div className="mt-2 flex justify-between text-[13px] text-decide-slate">
        <span>{q.left_label || t("clarify.low")}</span>
        <span>{q.right_label || t("clarify.high")}</span>
      </div>
    </div>
  );
}

function Choice({ q, value, onChange }) {
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {(q.options || []).map((opt) => {
        const active = value === opt;
        return (
          <button key={opt} data-testid={`choice-${q.id}`} onClick={() => onChange(opt)} className={`rounded-full px-4 py-2.5 text-sm font-medium transition-all ${active ? "bg-decide-forest text-white border border-decide-forest" : "bg-decide-surface text-decide-slate border border-decide-line hover:border-decide-ink hover:text-decide-ink"}`}>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export default function Clarify({ questions, answers, setAnswers, onSubmit }) {
  const { t } = useLang();
  const setAnswer = (id, val) => setAnswers((a) => ({ ...a, [id]: val }));
  const allAnswered = questions.every((q) => answers[q.id] !== undefined && answers[q.id] !== "");
  const count = questions.length;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <p className="decide-label">{t("clarify.label")}</p>
      <h1 className="mt-2 text-2xl sm:text-3xl font-light tracking-tight text-decide-ink leading-tight">
        {count === 1 ? t("clarify.needOne") : t("clarify.needMany", { n: count })}
      </h1>

      <div className="mt-7 space-y-4">
        {questions.map((q, i) => (
          <div key={q.id} className="decide-card" data-testid="clarify-question">
            <div className="flex items-baseline gap-2">
              <span className="text-decide-mute font-mono text-xs mt-0.5">{i + 1}</span>
              <p className="font-medium text-decide-ink text-[16px] leading-snug">{q.text}</p>
            </div>
            {q.type === "choice" ? (
              <Choice q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
            ) : (
              <Slider q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} t={t} />
            )}
          </div>
        ))}
      </div>

      <button data-testid="compare-btn" onClick={onSubmit} disabled={!allAnswered} className="btn-accent mt-8 w-full">
        {t("clarify.compare")} <ArrowRight size={18} />
      </button>
      {!allAnswered && <p className="mt-3 text-center text-[13px] text-decide-mute">{t("clarify.answerAll")}</p>}
    </motion.div>
  );
}
